import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FileJson, GitBranch, Loader2, Play, ScrollText } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { TwinneeWordmark } from '@/components/TwinneeWordmark';
import { flowHistoryPanelPaddingClass } from '@/components/creationFlows/FlowHistoryPanel';
import { TutorialHistorySidebar } from '@/components/learningTutorial/TutorialHistorySidebar';
import api from '@/lib/api';
import { LEARNING_TUTORIAL_ORCHESTRATION_TIMEOUT_MS } from '@/lib/learningTutorial/apiTimeouts';
import { ConceptTreeV3Panel } from '@/components/learningTutorial/ConceptTreeV3Panel';
import { ArtifactAuthoringV3Panel } from '@/components/learningTutorial/ArtifactAuthoringV3Panel';
import { ArtifactStudioV3Panel } from '@/components/learningTutorial/ArtifactStudioV3Panel';
import { ArtifactLayoutEngineV3Panel } from '@/components/learningTutorial/ArtifactLayoutEngineV3Panel';
import { ArtifactSvgRendererV3Panel } from '@/components/learningTutorial/ArtifactSvgRendererV3Panel';
import { ArtifactRraoV3Panel } from '@/components/learningTutorial/ArtifactRraoV3Panel';
import { ArtifactEdmV3Panel } from '@/components/learningTutorial/ArtifactEdmV3Panel';
import { ArtifactRenderingV3Panel } from '@/components/learningTutorial/ArtifactRenderingV3Panel';
import { ArtifactGenerationV3Panel } from '@/components/learningTutorial/ArtifactGenerationV3Panel';
import { ArtifactSpecificationV3Panel } from '@/components/learningTutorial/ArtifactSpecificationV3Panel';
import { CurriculumOptimizationV3Panel } from '@/components/learningTutorial/CurriculumOptimizationV3Panel';
import { KnowledgeAnalysisV3Panel } from '@/components/learningTutorial/KnowledgeAnalysisV3Panel';
import { SlideArchitectureV3Panel } from '@/components/learningTutorial/SlideArchitectureV3Panel';
import { Flow3PipelineOutlineDialog } from '@/components/learningTutorial/Flow3PipelineOutlineDialog';
import { FLOW3_PIPELINE_STEPS } from '@/lib/learningTutorial/flow3PipelineSteps';
import {
  flow3CanRunStep,
  flow3ProgressPct,
  flow3ResolveResumeStep,
  flow3StepComplete,
  flow3StepMeta,
  flow3StepsFrom,
} from '@/lib/learningTutorial/flow3PipelineRunner';
import {
  flow3StatusLabel,
  flow3UiStateFromProject,
  flow3ProjectPath,
  hydrateFlow3FormFromProject,
  isTutorialFlow3Project,
  readFlow3HistorySidebarOpen,
  writeFlow3HistorySidebarOpen,
} from '@/lib/learningTutorial/tutorialHistory';

import { TwinneeLogo } from '@/components/TwinneeLogo';
export function TutorialFlow3Page() {
  const navigate = useNavigate();
  const { projectId: routeProjectId } = useParams();
  const loadedRouteIdRef = useRef(null);
  const [tutorialName, setTutorialName] = useState('');
  const [topic, setTopic] = useState('');
  const [status, setStatus] = useState('idle');
  const [currentStep, setCurrentStep] = useState(null);
  const [result, setResult] = useState(null);
  const [knowledgeAnalysis, setKnowledgeAnalysis] = useState(null);
  const [courseStructure, setCourseStructure] = useState(null);
  const [slideArchitecture, setSlideArchitecture] = useState(null);
  const [curriculumOptimization, setCurriculumOptimization] = useState(null);
  const [artifactSpecification, setArtifactSpecification] = useState(null);
  const [artifactGeneration, setArtifactGeneration] = useState(null);
  const [artifactRendering, setArtifactRendering] = useState(null);
  const [artifactEdm, setArtifactEdm] = useState(null);
  const [artifactAuthoring, setArtifactAuthoring] = useState(null);
  const [artifactStudio, setArtifactStudio] = useState(null);
  const [artifactLayoutEngine, setArtifactLayoutEngine] = useState(null);
  const [artifactSvgRenderer, setArtifactSvgRenderer] = useState(null);
  const [artifactRrao, setArtifactRrao] = useState(null);
  const [projectId, setProjectId] = useState(null);
  const [showJson, setShowJson] = useState(false);
  const [outlineOpen, setOutlineOpen] = useState(false);
  const [pipelineError, setPipelineError] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(readFlow3HistorySidebarOpen);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);

  const pipelineLoading = status === 'working';

  const pipelineCtx = useMemo(
    () => ({
      tutorialName,
      topic,
      conceptTree: result,
      knowledgeAnalysis,
      courseStructure,
      slideArchitecture,
      curriculumOptimization,
      artifactSpecification,
      artifactGeneration,
      artifactRendering,
      artifactEdm,
      artifactAuthoring,
      artifactStudio,
      artifactLayoutEngine,
      artifactSvgRenderer,
      artifactRrao,
      pipelineLoading,
    }),
    [tutorialName, topic, result, knowledgeAnalysis, courseStructure, slideArchitecture, curriculumOptimization, artifactSpecification, artifactGeneration, artifactRendering, artifactEdm, artifactAuthoring, artifactStudio, artifactLayoutEngine, artifactSvgRenderer, artifactRrao, pipelineLoading],
  );

  const progressPct = useMemo(
    () => flow3ProgressPct({
      conceptTree: result,
      knowledgeAnalysis,
      courseStructure,
      slideArchitecture,
      curriculumOptimization,
      artifactSpecification,
      artifactGeneration,
      artifactRendering,
      artifactEdm,
      artifactAuthoring,
      artifactStudio,
      artifactLayoutEngine,
      artifactSvgRenderer,
      artifactRrao,
    }),
    [result, knowledgeAnalysis, courseStructure, slideArchitecture, curriculumOptimization, artifactSpecification, artifactGeneration, artifactRendering, artifactEdm, artifactAuthoring, artifactStudio, artifactLayoutEngine, artifactSvgRenderer, artifactRrao],
  );

  const showPipelinePanel = pipelineLoading || Boolean(
    result || knowledgeAnalysis || courseStructure || slideArchitecture || curriculumOptimization || artifactSpecification || artifactGeneration || artifactRendering || artifactEdm || artifactAuthoring || artifactStudio || artifactLayoutEngine || artifactSvgRenderer || artifactRrao,
  );

  const toggleHistory = useCallback(() => {
    setHistoryOpen((prev) => {
      const next = !prev;
      writeFlow3HistorySidebarOpen(next);
      return next;
    });
  }, []);

  const fetchProject = useCallback(async (pid) => {
    const { data } = await api.get(`/learning-tutorials/projects/${pid}`);
    return data.project;
  }, []);

  const commitProjectId = useCallback((id) => {
    if (!id) return;
    setProjectId(id);
    loadedRouteIdRef.current = id;
    if (routeProjectId !== id) {
      navigate(flow3ProjectPath(id), { replace: true });
    }
  }, [navigate, routeProjectId]);

  const applyProjectState = useCallback((full) => {
    hydrateFlow3FormFromProject(full, { setTutorialName, setTopic });
    setProjectId(full.id);
    loadedRouteIdRef.current = full.id;
    setResult(full.concept_tree_v3 || full.orchestration?.concept_tree_v3 || null);
    setKnowledgeAnalysis(
      full.knowledge_analysis_v3 || full.orchestration?.knowledge_analysis_v3 || null,
    );
    setCourseStructure(
      full.course_structure_v3 || full.orchestration?.course_structure_v3 || null,
    );
    setSlideArchitecture(
      full.slide_architecture_v3 || full.orchestration?.slide_architecture_v3 || null,
    );
    setCurriculumOptimization(
      full.curriculum_optimization_v3 || full.orchestration?.curriculum_optimization_v3 || null,
    );
    setArtifactSpecification(
      full.artifact_specification_v3 || full.orchestration?.artifact_specification_v3 || null,
    );
    setArtifactGeneration(
      full.artifact_generation_v3 || full.orchestration?.artifact_generation_v3 || null,
    );
    setArtifactRendering(
      full.artifact_rendering_v3 || full.orchestration?.artifact_rendering_v3 || null,
    );
    setArtifactEdm(
      full.artifact_edm_v3 || full.orchestration?.artifact_edm_v3 || null,
    );
    setArtifactAuthoring(
      full.artifact_authoring_v3 || full.orchestration?.artifact_authoring_v3 || null,
    );
    setArtifactStudio(
      full.artifact_studio_v3 || full.orchestration?.artifact_studio_v3 || null,
    );
    setArtifactLayoutEngine(
      full.artifact_layout_engine_v3 || full.orchestration?.artifact_layout_engine_v3 || null,
    );
    setArtifactSvgRenderer(
      full.artifact_svg_renderer_v3 || full.orchestration?.artifact_svg_renderer_v3 || null,
    );
    setArtifactRrao(
      full.artifact_rrao_v3 || full.orchestration?.artifact_rrao_v3 || null,
    );
    const opt = full.curriculum_optimization_v3 || full.orchestration?.curriculum_optimization_v3;
    if (opt?.updated_concept_tree) {
      setResult(opt.updated_concept_tree);
    }
    const ui = flow3UiStateFromProject(full);
    setCurrentStep(ui.currentStep);
    setStatus(ui.status);
    setPipelineError(
      ui.status === 'error' ? (full.last_pipeline_error || null) : null,
    );
    setOutlineOpen(false);
  }, []);

  const resetNewTutorial = useCallback(() => {
    loadedRouteIdRef.current = null;
    setProjectId(null);
    setTutorialName('');
    setTopic('');
    setStatus('idle');
    setCurrentStep(null);
    setResult(null);
    setKnowledgeAnalysis(null);
    setCourseStructure(null);
    setSlideArchitecture(null);
    setCurriculumOptimization(null);
    setArtifactSpecification(null);
    setArtifactGeneration(null);
    setArtifactRendering(null);
    setArtifactEdm(null);
    setArtifactAuthoring(null);
    setArtifactStudio(null);
    setArtifactLayoutEngine(null);
    setArtifactSvgRenderer(null);
    setArtifactRrao(null);
    setPipelineError(null);
    setOutlineOpen(false);
    setShowJson(false);
    if (routeProjectId) {
      navigate(flow3ProjectPath(), { replace: true });
    }
  }, [navigate, routeProjectId]);

  useEffect(() => {
    if (!routeProjectId) {
      return;
    }
    if (pipelineLoading) return;
    if (loadedRouteIdRef.current === routeProjectId && projectId === routeProjectId) {
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const full = await fetchProject(routeProjectId);
        if (cancelled) return;
        if (!isTutorialFlow3Project(full)) {
          toast.error('Not a Flow 3 project');
          navigate(flow3ProjectPath(), { replace: true });
          return;
        }
        applyProjectState(full);
      } catch (err) {
        if (cancelled) return;
        toast.error(err.response?.data?.detail || 'Could not load project');
        navigate(flow3ProjectPath(), { replace: true });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    routeProjectId,
    pipelineLoading,
    projectId,
    fetchProject,
    applyProjectState,
    navigate,
  ]);

  const loadHistoricalProject = async (summary) => {
    if (!summary?.id || summary.id === projectId) return;
    if (pipelineLoading) {
      toast.error('Wait for the current pipeline to finish');
      return;
    }
    try {
      const full = await fetchProject(summary.id);
      applyProjectState(full);
      navigate(flow3ProjectPath(full.id), { replace: true });
      toast.success('Loaded Flow 3 project from history');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load project');
    }
  };

  const runConceptTreeStep = useCallback(async ({ persist = true } = {}) => {
    const name = tutorialName.trim();
    const subj = topic.trim();
    if (!name) throw new Error('Tutorial name is required');
    if (subj.length < 2) throw new Error('Topic must be at least 2 characters');

    setCurrentStep('concept_tree_v3');
    setKnowledgeAnalysis(null);
    setCourseStructure(null);
    setSlideArchitecture(null);
    setCurriculumOptimization(null);
    setArtifactSpecification(null);
    setArtifactGeneration(null);
    setArtifactRendering(null);
    setArtifactEdm(null);
    setArtifactAuthoring(null);
    setArtifactStudio(null);
    setArtifactLayoutEngine(null);
    setArtifactSvgRenderer(null);
    setArtifactRrao(null);

    const createNewProject = persist && !projectId;
    const path = createNewProject
      ? '/learning-tutorials/flows/v3/projects'
      : '/learning-tutorials/flows/v3/concept-tree';
    const { data } = await api.post(
      path,
      { tutorial_name: name, topic: subj },
      { timeout: LEARNING_TUTORIAL_ORCHESTRATION_TIMEOUT_MS },
    );
    const tree = data.concept_tree_v3;
    setResult(tree);
    if (data.project?.id) {
      commitProjectId(data.project.id);
      if (data.project.knowledge_analysis_v3) {
        setKnowledgeAnalysis(data.project.knowledge_analysis_v3);
      }
      setHistoryRefreshKey((k) => k + 1);
    } else if (!persist) {
      setProjectId(null);
    } else if (projectId) {
      setHistoryRefreshKey((k) => k + 1);
    }
    if (tree?.source === 'fallback') {
      toast.message('Used fallback tree — check LLM configuration');
    }
    return {
      tree,
      projectId: data.project?.id || projectId,
    };
  }, [tutorialName, topic, projectId, commitProjectId]);

  const runKnowledgeAnalysisStep = useCallback(async (
    conceptTreeOverride = null,
    projectIdOverride = null,
  ) => {
    const tree = conceptTreeOverride || result;
    const activeProjectId = projectIdOverride ?? projectId;
    if (!tree?.concept_tree) {
      throw new Error('Generate a concept tree first');
    }

    setCurrentStep('knowledge_analysis_v3');

    const path = activeProjectId
      ? `/learning-tutorials/flows/v3/projects/${activeProjectId}/knowledge-analysis`
      : '/learning-tutorials/flows/v3/knowledge-analysis';
    const payload = activeProjectId ? undefined : { concept_tree_v3: tree };
    const { data } = await api.post(
      path,
      payload,
      { timeout: LEARNING_TUTORIAL_ORCHESTRATION_TIMEOUT_MS },
    );
    const report = data.knowledge_analysis_v3;
    setKnowledgeAnalysis(report);
    if (data.project?.id) {
      commitProjectId(data.project.id);
      setHistoryRefreshKey((k) => k + 1);
    }
    if (report?.source === 'fallback') {
      toast.message('Used fallback analysis — check LLM configuration');
    }
    return report;
  }, [result, projectId, commitProjectId]);

  const runCourseStructureStep = useCallback(async (
    conceptTreeOverride = null,
    analysisOverride = null,
    projectIdOverride = null,
  ) => {
    const tree = conceptTreeOverride || result;
    const analysis = analysisOverride || knowledgeAnalysis;
    const activeProjectId = projectIdOverride ?? projectId;
    if (!tree?.concept_tree || !analysis?.concepts?.length) {
      throw new Error('Concept tree and knowledge analysis required');
    }

    setCurrentStep('course_structure_v3');
    const path = activeProjectId
      ? `/learning-tutorials/flows/v3/projects/${activeProjectId}/course-structure`
      : '/learning-tutorials/flows/v3/course-structure';
    const payload = activeProjectId
      ? undefined
      : { concept_tree_v3: tree, knowledge_analysis_v3: analysis };
    const { data } = await api.post(path, payload, {
      timeout: LEARNING_TUTORIAL_ORCHESTRATION_TIMEOUT_MS,
    });
    const structure = data.course_structure_v3;
    setCourseStructure(structure);
    if (data.project?.id) {
      commitProjectId(data.project.id);
      setHistoryRefreshKey((k) => k + 1);
    }
    if (structure?.source === 'fallback') {
      toast.message('Used fallback course structure — check LLM configuration');
    }
    return structure;
  }, [result, knowledgeAnalysis, projectId, commitProjectId]);

  const runSlideArchitectureStep = useCallback(async (
    conceptTreeOverride = null,
    analysisOverride = null,
    structureOverride = null,
    projectIdOverride = null,
  ) => {
    const tree = conceptTreeOverride || result;
    const analysis = analysisOverride || knowledgeAnalysis;
    const structure = structureOverride || courseStructure;
    const activeProjectId = projectIdOverride ?? projectId;
    if (!structure?.modules?.length) {
      throw new Error('Course structure required');
    }

    setCurrentStep('slide_architecture_v3');
    const path = activeProjectId
      ? `/learning-tutorials/flows/v3/projects/${activeProjectId}/slide-architecture`
      : '/learning-tutorials/flows/v3/slide-architecture';
    const payload = activeProjectId
      ? undefined
      : {
        concept_tree_v3: tree,
        knowledge_analysis_v3: analysis,
        course_structure_v3: structure,
      };
    const { data } = await api.post(path, payload, {
      timeout: LEARNING_TUTORIAL_ORCHESTRATION_TIMEOUT_MS,
    });
    const slides = data.slide_architecture_v3;
    setSlideArchitecture(slides);
    if (data.project?.id) {
      commitProjectId(data.project.id);
      setHistoryRefreshKey((k) => k + 1);
    }
    if (slides?.source === 'fallback') {
      toast.message('Used fallback slide architecture — check LLM configuration');
    }
    return slides;
  }, [result, knowledgeAnalysis, courseStructure, projectId, commitProjectId]);

  const runCurriculumOptimizationStep = useCallback(async (
    conceptTreeOverride = null,
    analysisOverride = null,
    structureOverride = null,
    slidesOverride = null,
    projectIdOverride = null,
  ) => {
    const tree = conceptTreeOverride || result;
    const analysis = analysisOverride || knowledgeAnalysis;
    const structure = structureOverride || courseStructure;
    const slides = slidesOverride || slideArchitecture;
    const activeProjectId = projectIdOverride ?? projectId;
    if (!slides?.slides?.length) {
      throw new Error('Slide architecture required');
    }

    setCurrentStep('curriculum_optimization_v3');
    const path = activeProjectId
      ? `/learning-tutorials/flows/v3/projects/${activeProjectId}/curriculum-optimization`
      : '/learning-tutorials/flows/v3/curriculum-optimization';
    const payload = activeProjectId
      ? undefined
      : {
        concept_tree_v3: tree,
        knowledge_analysis_v3: analysis,
        course_structure_v3: structure,
        slide_architecture_v3: slides,
      };
    const { data } = await api.post(path, payload, {
      timeout: LEARNING_TUTORIAL_ORCHESTRATION_TIMEOUT_MS,
    });
    const optimization = data.curriculum_optimization_v3;
    setCurriculumOptimization(optimization);
    if (optimization?.updated_concept_tree) {
      setResult(optimization.updated_concept_tree);
    }
    if (optimization?.updated_course_structure) {
      setCourseStructure(optimization.updated_course_structure);
    }
    if (optimization?.updated_slide_architecture) {
      setSlideArchitecture(optimization.updated_slide_architecture);
    }
    if (data.project?.id) {
      commitProjectId(data.project.id);
      setHistoryRefreshKey((k) => k + 1);
    }
    if (optimization?.source === 'fallback') {
      toast.message('Used fallback optimization — check LLM configuration');
    }
    return optimization;
  }, [result, knowledgeAnalysis, courseStructure, slideArchitecture, projectId, commitProjectId]);

  const runArtifactSpecificationStep = useCallback(async (
    conceptTreeOverride = null,
    analysisOverride = null,
    slidesOverride = null,
    optimizationOverride = null,
    projectIdOverride = null,
  ) => {
    const tree = conceptTreeOverride || result;
    const analysis = analysisOverride || knowledgeAnalysis;
    const slides = slidesOverride || slideArchitecture;
    const optimization = optimizationOverride || curriculumOptimization;
    const activeProjectId = projectIdOverride ?? projectId;
    const resolvedSlides = optimization?.updated_slide_architecture || slides;
    if (!resolvedSlides?.slides?.length) {
      throw new Error('Optimized slide architecture required — run Step 3.5 first');
    }

    setCurrentStep('artifact_specification_v3');
    const path = activeProjectId
      ? `/learning-tutorials/flows/v3/projects/${activeProjectId}/artifact-specification`
      : '/learning-tutorials/flows/v3/artifact-specification';
    const payload = activeProjectId
      ? undefined
      : {
        concept_tree_v3: tree,
        knowledge_analysis_v3: analysis,
        slide_architecture_v3: resolvedSlides,
        curriculum_optimization_v3: optimization,
      };
    const { data } = await api.post(path, payload, {
      timeout: LEARNING_TUTORIAL_ORCHESTRATION_TIMEOUT_MS,
    });
    const specs = data.artifact_specification_v3;
    setArtifactSpecification(specs);
    if (data.project?.id) {
      commitProjectId(data.project.id);
      setHistoryRefreshKey((k) => k + 1);
    }
    if (specs?.source === 'fallback') {
      toast.message('Used fallback artifact specs — check LLM configuration');
    }
    return specs;
  }, [result, knowledgeAnalysis, slideArchitecture, curriculumOptimization, projectId, commitProjectId]);

  const runArtifactGenerationStep = useCallback(async (
    conceptTreeOverride = null,
    analysisOverride = null,
    specsOverride = null,
    projectIdOverride = null,
  ) => {
    const tree = conceptTreeOverride || result;
    const analysis = analysisOverride || knowledgeAnalysis;
    const specs = specsOverride || artifactSpecification;
    const activeProjectId = projectIdOverride ?? projectId;
    if (!specs?.slide_specifications?.length) {
      throw new Error('Artifact specification required — run Step 4 first');
    }

    setCurrentStep('artifact_generation_v3');
    const path = activeProjectId
      ? `/learning-tutorials/flows/v3/projects/${activeProjectId}/artifact-generation`
      : '/learning-tutorials/flows/v3/artifact-generation';
    const payload = activeProjectId
      ? undefined
      : {
        concept_tree_v3: tree,
        knowledge_analysis_v3: analysis,
        artifact_specification_v3: specs,
        course_structure_v3: courseStructure,
        slide_architecture_v3: slideArchitecture,
        curriculum_optimization_v3: curriculumOptimization,
      };
    const { data } = await api.post(path, payload, {
      timeout: LEARNING_TUTORIAL_ORCHESTRATION_TIMEOUT_MS,
    });
    const akos = data.artifact_generation_v3;
    setArtifactGeneration(akos);
    if (data.project?.id) {
      commitProjectId(data.project.id);
      setHistoryRefreshKey((k) => k + 1);
    }
    if (akos?.source === 'fallback') {
      toast.message('Used fallback AKO generation — check LLM configuration');
    }
    return akos;
  }, [result, knowledgeAnalysis, artifactSpecification, courseStructure, slideArchitecture, curriculumOptimization, projectId, commitProjectId]);

  const runArtifactRenderingStep = useCallback(async (
    akosOverride = null,
    treeOverride = null,
    projectIdOverride = null,
  ) => {
    const akos = akosOverride || artifactGeneration;
    const tree = treeOverride || result;
    const activeProjectId = projectIdOverride ?? projectId;
    if (!akos?.artifact_knowledge_objects?.length) {
      throw new Error('AKOs required — run Step 5A first');
    }

    setCurrentStep('artifact_rendering_v3');
    const path = activeProjectId
      ? `/learning-tutorials/flows/v3/projects/${activeProjectId}/artifact-rendering`
      : '/learning-tutorials/flows/v3/artifact-rendering';
    const payload = activeProjectId
      ? undefined
      : {
        artifact_generation_v3: akos,
        concept_tree_v3: tree,
      };
    const { data } = await api.post(path, payload, {
      timeout: LEARNING_TUTORIAL_ORCHESTRATION_TIMEOUT_MS,
    });
    const raos = data.artifact_rendering_v3;
    setArtifactRendering(raos);
    if (data.project?.id) {
      commitProjectId(data.project.id);
      setHistoryRefreshKey((k) => k + 1);
    }
    if (raos?.source === 'fallback') {
      toast.message('Used fallback ARP generation — check LLM configuration');
    }
    return raos;
  }, [artifactGeneration, result, projectId, commitProjectId]);

  const runArtifactEdmStep = useCallback(async (
    raosOverride = null,
    akosOverride = null,
    treeOverride = null,
    projectIdOverride = null,
  ) => {
    const raos = raosOverride || artifactRendering;
    const akos = akosOverride || artifactGeneration;
    const tree = treeOverride || result;
    const activeProjectId = projectIdOverride ?? projectId;
    if (!raos?.rendered_artifact_objects?.length) {
      throw new Error('ARPs required — run Step 5B first');
    }

    setCurrentStep('artifact_edm_v3');
    const path = activeProjectId
      ? `/learning-tutorials/flows/v3/projects/${activeProjectId}/artifact-edm`
      : '/learning-tutorials/flows/v3/artifact-edm';
    const payload = activeProjectId
      ? undefined
      : {
        artifact_rendering_v3: raos,
        artifact_generation_v3: akos,
        concept_tree_v3: tree,
      };
    const { data } = await api.post(path, payload, {
      timeout: LEARNING_TUTORIAL_ORCHESTRATION_TIMEOUT_MS,
    });
    const edms = data.artifact_edm_v3;
    setArtifactEdm(edms);
    if (data.project?.id) {
      commitProjectId(data.project.id);
      setHistoryRefreshKey((k) => k + 1);
    }
    if (edms?.source === 'deterministic') {
      toast.message('Used deterministic EDM conversion — LLM optional for enrichment');
    }
    return edms;
  }, [artifactRendering, artifactGeneration, result, projectId, commitProjectId]);

  const runArtifactAuthoringStep = useCallback(async (
    edmsOverride = null,
    projectIdOverride = null,
  ) => {
    const edms = edmsOverride || artifactEdm;
    const activeProjectId = projectIdOverride ?? projectId;
    if (!edms?.editable_diagram_models?.length) {
      throw new Error('EDMs required — run Step 5C.1 first');
    }

    setCurrentStep('artifact_authoring_v3');
    const path = activeProjectId
      ? `/learning-tutorials/flows/v3/projects/${activeProjectId}/artifact-authoring`
      : '/learning-tutorials/flows/v3/artifact-authoring';
    const payload = activeProjectId
      ? undefined
      : { artifact_edm_v3: edms };
    const { data } = await api.post(path, payload, {
      timeout: LEARNING_TUTORIAL_ORCHESTRATION_TIMEOUT_MS,
    });
    const authoring = data.artifact_authoring_v3;
    setArtifactAuthoring(authoring);
    if (data.project?.id) {
      commitProjectId(data.project.id);
      setHistoryRefreshKey((k) => k + 1);
    }
    return authoring;
  }, [artifactEdm, projectId, commitProjectId]);

  const runArtifactStudioStep = useCallback(async (
    authoringOverride = null,
    projectIdOverride = null,
  ) => {
    const authoring = authoringOverride || artifactAuthoring;
    const activeProjectId = projectIdOverride ?? projectId;
    if (!authoring?.published_artifacts?.length) {
      throw new Error('Published artifacts required — run Step 5C.2 first');
    }

    setCurrentStep('artifact_studio_v3');
    const path = activeProjectId
      ? `/learning-tutorials/flows/v3/projects/${activeProjectId}/artifact-studio`
      : '/learning-tutorials/flows/v3/artifact-studio';
    const payload = activeProjectId
      ? undefined
      : { artifact_authoring_v3: authoring };
    const { data } = await api.post(path, payload, {
      timeout: LEARNING_TUTORIAL_ORCHESTRATION_TIMEOUT_MS,
    });
    const studio = data.artifact_studio_v3;
    setArtifactStudio(studio);
    if (data.project?.id) {
      commitProjectId(data.project.id);
      setHistoryRefreshKey((k) => k + 1);
    }
    return studio;
  }, [artifactAuthoring, projectId, commitProjectId]);

  const runArtifactLayoutEngineStep = useCallback(async (
    studioOverride = null,
    projectIdOverride = null,
  ) => {
    const studio = studioOverride || artifactStudio;
    const activeProjectId = projectIdOverride ?? projectId;
    if (!studio?.studio_entries?.length) {
      throw new Error('Studio entries required — run Step 5C.3 first');
    }

    setCurrentStep('artifact_layout_engine_v3');
    const path = activeProjectId
      ? `/learning-tutorials/flows/v3/projects/${activeProjectId}/artifact-layout-engine`
      : '/learning-tutorials/flows/v3/artifact-layout-engine';
    const payload = activeProjectId
      ? undefined
      : { artifact_studio_v3: studio };
    const { data } = await api.post(path, payload, {
      timeout: LEARNING_TUTORIAL_ORCHESTRATION_TIMEOUT_MS,
    });
    const layoutEngine = data.artifact_layout_engine_v3;
    setArtifactLayoutEngine(layoutEngine);
    if (data.project?.id) {
      commitProjectId(data.project.id);
      setHistoryRefreshKey((k) => k + 1);
    }
    return layoutEngine;
  }, [artifactStudio, projectId, commitProjectId]);

  const runArtifactSvgRendererStep = useCallback(async (
    layoutOverride = null,
    projectIdOverride = null,
  ) => {
    const layoutEngine = layoutOverride || artifactLayoutEngine;
    const activeProjectId = projectIdOverride ?? projectId;
    if (!layoutEngine?.layout_entries?.length) {
      throw new Error('Layout entries required — run Step 5C.4 first');
    }

    setCurrentStep('artifact_svg_renderer_v3');
    const path = activeProjectId
      ? `/learning-tutorials/flows/v3/projects/${activeProjectId}/artifact-svg-renderer`
      : '/learning-tutorials/flows/v3/artifact-svg-renderer';
    const payload = activeProjectId
      ? undefined
      : { artifact_layout_engine_v3: layoutEngine };
    const { data } = await api.post(path, payload, {
      timeout: LEARNING_TUTORIAL_ORCHESTRATION_TIMEOUT_MS,
    });
    const svgRenderer = data.artifact_svg_renderer_v3;
    setArtifactSvgRenderer(svgRenderer);
    if (data.project?.id) {
      commitProjectId(data.project.id);
      setHistoryRefreshKey((k) => k + 1);
    }
    return svgRenderer;
  }, [artifactLayoutEngine, projectId, commitProjectId]);

  const runArtifactRraoStep = useCallback(async (
    svgRendererOverride = null,
    renderingOverride = null,
    projectIdOverride = null,
  ) => {
    const svgRenderer = svgRendererOverride || artifactSvgRenderer;
    const rendering = renderingOverride || artifactRendering;
    const activeProjectId = projectIdOverride ?? projectId;
    if (!svgRenderer?.render_entries?.length) {
      throw new Error('SVG render entries required — run Step 5C.5 first');
    }

    setCurrentStep('artifact_rrao_v3');
    const path = activeProjectId
      ? `/learning-tutorials/flows/v3/projects/${activeProjectId}/artifact-rrao`
      : '/learning-tutorials/flows/v3/artifact-rrao';
    const payload = activeProjectId
      ? undefined
      : {
        artifact_svg_renderer_v3: svgRenderer,
        artifact_rendering_v3: rendering || undefined,
      };
    const { data } = await api.post(path, payload, {
      timeout: LEARNING_TUTORIAL_ORCHESTRATION_TIMEOUT_MS,
    });
    const rrao = data.artifact_rrao_v3;
    setArtifactRrao(rrao);
    if (data.project?.id) {
      commitProjectId(data.project.id);
      setHistoryRefreshKey((k) => k + 1);
    }
    return rrao;
  }, [artifactSvgRenderer, artifactRendering, projectId, commitProjectId]);

  const runPipelineStep = useCallback(async (
    stepId,
    { continueAfter = false, persistTree = false } = {},
  ) => {
    if (pipelineLoading) return;

    const steps = continueAfter ? flow3StepsFrom(stepId) : [stepId];
    if (!steps.length) return;

    const firstCheck = flow3CanRunStep(steps[0], pipelineCtx);
    if (!firstCheck.ok) {
      toast.error(firstCheck.reason);
      return;
    }

    setPipelineError(null);
    setStatus('working');
    setOutlineOpen(true);

    try {
      let latestTree = result;
      let latestAnalysis = knowledgeAnalysis;
      let latestStructure = courseStructure;
      let latestSlides = slideArchitecture;
      let latestOptimization = curriculumOptimization;
      let latestSpecs = artifactSpecification;
      let latestAkos = artifactGeneration;
      let latestRaos = artifactRendering;
      let latestEdms = artifactEdm;
      let latestAuthoring = artifactAuthoring;
      let latestStudio = artifactStudio;
      let latestLayoutEngine = artifactLayoutEngine;
      let latestSvgRenderer = artifactSvgRenderer;
      let latestProjectId = projectId;
      for (const id of steps) {
        const check = flow3CanRunStep(id, {
          ...pipelineCtx,
          conceptTree: latestTree,
          knowledgeAnalysis: latestAnalysis,
          courseStructure: latestStructure,
          slideArchitecture: latestSlides,
          curriculumOptimization: latestOptimization,
          artifactSpecification: latestSpecs,
          artifactGeneration: latestAkos,
          artifactRendering: latestRaos,
          artifactEdm: latestEdms,
          artifactAuthoring: latestAuthoring,
          artifactStudio: latestStudio,
          artifactLayoutEngine: latestLayoutEngine,
          artifactSvgRenderer: latestSvgRenderer,
        });
        if (!check.ok) {
          throw new Error(check.reason);
        }

        if (id === 'concept_tree_v3') {
          const shouldPersist = true;
          const out = await runConceptTreeStep({ persist: shouldPersist });
          latestTree = out.tree;
          latestAnalysis = null;
          latestStructure = null;
          latestSlides = null;
          latestOptimization = null;
          latestSpecs = null;
          latestAkos = null;
          if (out.projectId) latestProjectId = out.projectId;
          toast.success('Concept tree generated');
        } else if (id === 'knowledge_analysis_v3') {
          latestAnalysis = await runKnowledgeAnalysisStep(latestTree, latestProjectId);
          latestStructure = null;
          latestSlides = null;
          latestOptimization = null;
          latestSpecs = null;
          toast.success('Knowledge analysis complete');
        } else if (id === 'course_structure_v3') {
          latestStructure = await runCourseStructureStep(latestTree, latestAnalysis, latestProjectId);
          latestSlides = null;
          latestOptimization = null;
          latestSpecs = null;
          toast.success('Course structure ready');
        } else if (id === 'slide_architecture_v3') {
          latestSlides = await runSlideArchitectureStep(
            latestTree, latestAnalysis, latestStructure, latestProjectId,
          );
          toast.success('Slide architecture ready');
        } else if (id === 'curriculum_optimization_v3') {
          latestOptimization = await runCurriculumOptimizationStep(
            latestTree, latestAnalysis, latestStructure, latestSlides, latestProjectId,
          );
          if (latestOptimization?.updated_slide_architecture) {
            latestSlides = latestOptimization.updated_slide_architecture;
          }
          toast.success('Curriculum optimization complete');
        } else if (id === 'artifact_specification_v3') {
          latestSpecs = await runArtifactSpecificationStep(
            latestTree, latestAnalysis, latestSlides, latestOptimization, latestProjectId,
          );
          toast.success('Artifact specifications ready');
        } else if (id === 'artifact_generation_v3') {
          latestAkos = await runArtifactGenerationStep(
            latestTree, latestAnalysis, latestSpecs, latestProjectId,
          );
          toast.success('Artifact Knowledge Objects ready');
        } else if (id === 'artifact_rendering_v3') {
          latestRaos = await runArtifactRenderingStep(latestAkos, latestTree, latestProjectId);
          toast.success('Artifact Rendering Plans ready');
        } else if (id === 'artifact_edm_v3') {
          latestEdms = await runArtifactEdmStep(latestRaos, latestAkos, latestTree, latestProjectId);
          toast.success('Editable Diagram Models ready');
        } else if (id === 'artifact_authoring_v3') {
          latestAuthoring = await runArtifactAuthoringStep(latestEdms, latestProjectId);
          toast.success('Artifact Authoring API ready');
        } else if (id === 'artifact_studio_v3') {
          latestStudio = await runArtifactStudioStep(latestAuthoring, latestProjectId);
          toast.success('Artifact Authoring Studio ready');
        } else if (id === 'artifact_layout_engine_v3') {
          latestLayoutEngine = await runArtifactLayoutEngineStep(latestStudio, latestProjectId);
          toast.success('Layout Engine Service ready');
        } else if (id === 'artifact_svg_renderer_v3') {
          latestSvgRenderer = await runArtifactSvgRendererStep(latestLayoutEngine, latestProjectId);
          toast.success('SVG Rendering Engine ready');
        } else if (id === 'artifact_rrao_v3') {
          await runArtifactRraoStep(latestSvgRenderer, latestRaos, latestProjectId);
          toast.success('Runtime Rendered Artifact Objects ready');
        }
      }
      setStatus('ready');
      setCurrentStep(null);
    } catch (err) {
      setStatus('error');
      setCurrentStep(null);
      const msg = err.response?.data?.detail || err.message || 'Pipeline step failed';
      setPipelineError(msg);
      toast.error(msg);
    }
  }, [
    pipelineLoading,
    pipelineCtx,
    result,
    knowledgeAnalysis,
    courseStructure,
    slideArchitecture,
    curriculumOptimization,
    artifactSpecification,
    artifactGeneration,
    artifactRendering,
    artifactEdm,
    artifactAuthoring,
    artifactStudio,
    projectId,
    runConceptTreeStep,
    runKnowledgeAnalysisStep,
    runCourseStructureStep,
    runSlideArchitectureStep,
    runCurriculumOptimizationStep,
    runArtifactSpecificationStep,
    runArtifactGenerationStep,
    runArtifactRenderingStep,
    runArtifactEdmStep,
    runArtifactAuthoringStep,
    runArtifactStudioStep,
    runArtifactLayoutEngineStep,
    runArtifactSvgRendererStep,
    runArtifactRraoStep,
  ]);

  const runPipelineFrom = useCallback(
    (stepId) => runPipelineStep(stepId, { continueAfter: true, persistTree: true }),
    [runPipelineStep],
  );

  const resumeStepId = useMemo(
    () => flow3ResolveResumeStep({
      conceptTree: result,
      knowledgeAnalysis,
      courseStructure,
      slideArchitecture,
      curriculumOptimization,
      artifactSpecification,
      artifactGeneration,
      artifactRendering,
      artifactEdm,
      artifactAuthoring,
      artifactStudio,
      artifactLayoutEngine,
      artifactSvgRenderer,
      artifactRrao,
    }),
    [result, knowledgeAnalysis, courseStructure, slideArchitecture, curriculumOptimization, artifactSpecification, artifactGeneration, artifactRendering, artifactEdm, artifactAuthoring, artifactStudio, artifactLayoutEngine, artifactSvgRenderer, artifactRrao],
  );

  return (
    <div
      className={`relative min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-slate-100 transition-[padding] duration-200 ease-out ${flowHistoryPanelPaddingClass(historyOpen)}`}
    >
      <header className="border-b border-slate-800 px-6 py-4 flex items-center justify-between">
        <TwinneeWordmark className="h-8" />
        <span className="text-sm text-slate-400 flex items-center gap-2">
          <GitBranch className="w-4 h-4 text-violet-400" />
          Tutorial Flow 3 — Concept Tree
          {projectId ? (
            <span className="text-[10px] font-mono text-violet-300/80 border border-violet-500/25 rounded px-1.5 py-0.5">
              {projectId.slice(0, 8)}…
            </span>
          ) : null}
        </span>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="text-3xl font-bold mb-2">Universal concept tree</h1>
          <p className="text-slate-400 mb-8 max-w-2xl">
            Step 0: concept tree. Step 1: intelligence report. Steps 2–3: course structure and slide
            architecture. Step 3.5: optimize. Step 4: specs. Step 5: Artifact Knowledge Objects (AKOs).
            Each tutorial is saved to a unique project URL so multiple tabs stay isolated.
          </p>

          {showPipelinePanel ? (
            <div className="mb-6 rounded-xl border border-violet-500/25 bg-black/40 p-4">
              <div className="flex items-center justify-between mb-2 gap-3">
                <span className="text-sm text-violet-200/90">Pipeline progress</span>
                <span className="text-sm text-slate-400 tabular-nums">{progressPct}%</span>
              </div>
              <Progress value={progressPct} className="h-2 bg-slate-800" />
              <div className="flex flex-wrap gap-2 mt-3">
                {FLOW3_PIPELINE_STEPS.map((step) => {
                  const done = flow3StepComplete(step.id, {
                    conceptTree: result,
                    knowledgeAnalysis,
                    courseStructure,
                    slideArchitecture,
                    curriculumOptimization,
                    artifactSpecification,
                    artifactGeneration,
                    artifactRendering,
                    artifactEdm,
                  });
                  const active = pipelineLoading && currentStep === step.id;
                  return (
                    <span
                      key={step.id}
                      className={`text-[10px] px-2 py-0.5 rounded border ${
                        active
                          ? 'border-amber-500/40 text-amber-300 bg-amber-500/10'
                          : done
                            ? 'border-emerald-500/30 text-emerald-300 bg-emerald-500/10'
                            : 'border-slate-700 text-slate-500'
                      }`}
                    >
                      {step.shortLabel}
                    </span>
                  );
                })}
              </div>
            </div>
          ) : null}

          <div className="space-y-4 rounded-2xl border border-slate-700 bg-slate-900/60 p-6 mb-8">
            <div>
              <Label>Tutorial name</Label>
              <Input
                value={tutorialName}
                onChange={(e) => setTutorialName(e.target.value)}
                placeholder="e.g. Mastering Binary Search"
                className="mt-1 bg-slate-800"
              />
            </div>
            <div>
              <Label>Topic</Label>
              <Input
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="e.g. Binary Search"
                className="mt-1 bg-slate-800"
              />
            </div>
            <div className="flex flex-wrap gap-3 pt-2">
              <Button
                type="button"
                disabled={pipelineLoading}
                onClick={() => runPipelineStep('concept_tree_v3', { persistTree: true })}
                className="bg-violet-600 hover:bg-violet-500"
              >
                {pipelineLoading && currentStep === 'concept_tree_v3' ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <TwinneeLogo className="w-4 h-4 mr-2" />
                )}
                Step 0 only
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={pipelineLoading || !flow3CanRunStep('knowledge_analysis_v3', pipelineCtx).ok}
                onClick={() => runPipelineStep('knowledge_analysis_v3')}
                className="border-amber-500/40 text-amber-200"
              >
                {pipelineLoading && currentStep === 'knowledge_analysis_v3' ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <ScrollText className="w-4 h-4 mr-2" />
                )}
                Step 1 only
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={pipelineLoading || !flow3CanRunStep('concept_tree_v3', pipelineCtx).ok}
                onClick={() => runPipelineFrom('concept_tree_v3')}
                className="border-emerald-500/40 text-emerald-200"
              >
                {pipelineLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Play className="w-4 h-4 mr-2" />
                )}
                Run full pipeline
              </Button>
              {resumeStepId && result ? (
                <Button
                  type="button"
                  variant="outline"
                  disabled={pipelineLoading}
                  onClick={() => runPipelineFrom(resumeStepId)}
                  className="border-sky-500/40 text-sky-200"
                >
                  Continue from {flow3StepMeta(resumeStepId).shortLabel}
                </Button>
              ) : null}
              {(result || knowledgeAnalysis || courseStructure || slideArchitecture || curriculumOptimization || artifactSpecification || artifactGeneration || artifactRendering || artifactEdm) ? (
                <Button
                  type="button"
                  variant="outline"
                  className="border-slate-600"
                  onClick={() => setOutlineOpen(true)}
                >
                  <ScrollText className="w-4 h-4 mr-2" />
                  Pipeline outline
                </Button>
              ) : null}
            </div>
            {pipelineError ? (
              <p className="text-xs text-red-400/90 rounded border border-red-500/25 bg-red-500/10 px-2 py-1.5">
                {pipelineError}
              </p>
            ) : null}
          </div>

          {result ? (
            <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-6 space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h2 className="text-lg font-semibold text-violet-100">{result.tutorial_name}</h2>
                  <p className="text-sm text-slate-400">{result.topic}</p>
                  {result.source ? (
                    <p className="text-[10px] text-slate-500 mt-1">Concept tree source: {result.source}</p>
                  ) : null}
                  {knowledgeAnalysis?.source ? (
                    <p className="text-[10px] text-slate-500">
                      Analysis source: {knowledgeAnalysis.source}
                      {' · '}
                      {knowledgeAnalysis.concept_count} concepts analyzed
                    </p>
                  ) : null}
                  {artifactRrao?.total_rrao_entries ? (
                    <p className="text-[10px] text-violet-400/90">
                      {artifactRrao.total_rrao_entries} Runtime Rendered Artifact Objects packaged
                    </p>
                  ) : artifactStudio?.total_studio_entries ? (
                    <p className="text-[10px] text-violet-400/90">
                      {artifactStudio.total_studio_entries} artifacts ready in Authoring Studio
                    </p>
                  ) : artifactAuthoring?.total_published ? (
                    <p className="text-[10px] text-cyan-400/90">
                      {artifactAuthoring.total_published} artifacts published to Authoring API
                    </p>
                  ) : artifactEdm?.total_edms ? (
                    <p className="text-[10px] text-violet-400/90">
                      {artifactEdm.total_edms} Editable Diagram Models generated
                    </p>
                  ) : (artifactRendering?.total_arps ?? artifactRendering?.total_raos) ? (
                    <p className="text-[10px] text-sky-400/90">
                      {artifactRendering.total_arps ?? artifactRendering.total_raos} Artifact Rendering Plans generated
                    </p>
                  ) : artifactGeneration?.total_akos ? (
                    <p className="text-[10px] text-emerald-400/90">
                      {artifactGeneration.total_akos} Artifact Knowledge Objects generated
                    </p>
                  ) : artifactSpecification?.total_artifact_specs ? (
                    <p className="text-[10px] text-sky-400/90">
                      {artifactSpecification.total_artifact_specs} artifact specs across {artifactSpecification.total_slides} slides
                    </p>
                  ) : curriculumOptimization?.final_curriculum_readiness_score != null ? (
                    <p className="text-[10px] text-violet-400/90">
                      Curriculum readiness: {curriculumOptimization.final_curriculum_readiness_score}
                      {' · '}
                      {curriculumOptimization.updated_slide_architecture?.total_slides || 0} slides
                    </p>
                  ) : slideArchitecture?.total_slides ? (
                    <p className="text-[10px] text-slate-500">
                      {slideArchitecture.total_slides} slides planned
                    </p>
                  ) : null}
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="text-xs"
                    onClick={() => setShowJson((v) => !v)}
                  >
                    <FileJson className="w-3.5 h-3.5 mr-1" />
                    {showJson ? 'Outline view' : 'JSON'}
                  </Button>
                  {projectId ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="text-xs border-emerald-500/40 text-emerald-300"
                      onClick={() => navigate(`/learning/${projectId}/editor`)}
                    >
                      Open in editor
                    </Button>
                  ) : null}
                </div>
              </div>
              {showJson ? (
                <pre className="text-xs font-mono text-emerald-100/90 bg-slate-950 border border-slate-700 rounded-lg p-4 overflow-auto max-h-[70vh] whitespace-pre-wrap">
                  {JSON.stringify(
                    {
                      concept_tree_v3: result,
                      knowledge_analysis_v3: knowledgeAnalysis,
                      course_structure_v3: courseStructure,
                      slide_architecture_v3: slideArchitecture,
                      curriculum_optimization_v3: curriculumOptimization,
                      artifact_specification_v3: artifactSpecification,
                      artifact_generation_v3: artifactGeneration,
                      artifact_rendering_v3: artifactRendering,
                      artifact_edm_v3: artifactEdm,
                      artifact_authoring_v3: artifactAuthoring,
                      artifact_studio_v3: artifactStudio,
                      artifact_layout_engine_v3: artifactLayoutEngine,
                      artifact_svg_renderer_v3: artifactSvgRenderer,
                      artifact_rrao_v3: artifactRrao,
                    },
                    null,
                    2,
                  )}
                </pre>
              ) : artifactRrao ? (
                <ArtifactRraoV3Panel data={artifactRrao} />
              ) : artifactSvgRenderer ? (
                <ArtifactSvgRendererV3Panel data={artifactSvgRenderer} />
              ) : artifactLayoutEngine ? (
                <ArtifactLayoutEngineV3Panel data={artifactLayoutEngine} />
              ) : artifactStudio ? (
                <ArtifactStudioV3Panel data={artifactStudio} />
              ) : artifactAuthoring ? (
                <ArtifactAuthoringV3Panel data={artifactAuthoring} />
              ) : artifactEdm ? (
                <ArtifactEdmV3Panel data={artifactEdm} />
              ) : artifactRendering ? (
                <ArtifactRenderingV3Panel data={artifactRendering} />
              ) : artifactGeneration ? (
                <ArtifactGenerationV3Panel data={artifactGeneration} />
              ) : artifactSpecification ? (
                <ArtifactSpecificationV3Panel data={artifactSpecification} />
              ) : curriculumOptimization ? (
                <CurriculumOptimizationV3Panel data={curriculumOptimization} />
              ) : slideArchitecture ? (
                <SlideArchitectureV3Panel data={slideArchitecture} />
              ) : knowledgeAnalysis ? (
                <KnowledgeAnalysisV3Panel
                  data={knowledgeAnalysis}
                  pipelineSteps={FLOW3_PIPELINE_STEPS}
                />
              ) : (
                <ConceptTreeV3Panel data={result} />
              )}
            </div>
          ) : null}
        </motion.div>
      </main>

      <TutorialHistorySidebar
        open={historyOpen}
        onToggle={toggleHistory}
        activeProjectId={projectId}
        onSelectProject={loadHistoricalProject}
        onNewTutorial={resetNewTutorial}
        refreshKey={historyRefreshKey}
        filterProject={isTutorialFlow3Project}
        title="Flow 3 history"
        description="Concept tree and knowledge analysis projects. Click one to reload its form and pipeline output."
        newLabel="New Flow 3"
        collapsedLabel="Flow 3"
        emptyMessage="No Flow 3 projects yet. Generate and save one to see it here."
        testId="flow3-history-sidebar"
        getStatusLabel={flow3StatusLabel}
        activeClassName="bg-violet-500/15 border-violet-500/40"
        activeTitleClassName="text-violet-100"
      />

      <Flow3PipelineOutlineDialog
        open={outlineOpen}
        onOpenChange={setOutlineOpen}
        conceptTree={result}
        knowledgeAnalysis={knowledgeAnalysis}
        courseStructure={courseStructure}
        slideArchitecture={slideArchitecture}
        curriculumOptimization={curriculumOptimization}
        artifactSpecification={artifactSpecification}
        artifactGeneration={artifactGeneration}
        artifactRendering={artifactRendering}
        artifactEdm={artifactEdm}
        artifactAuthoring={artifactAuthoring}
        artifactStudio={artifactStudio}
        artifactLayoutEngine={artifactLayoutEngine}
        artifactSvgRenderer={artifactSvgRenderer}
        artifactRrao={artifactRrao}
        pipelineStatus={status}
        currentStepId={currentStep}
        initialStepId={currentStep || resumeStepId || 'concept_tree_v3'}
        pipelineError={pipelineError}
        tutorialName={tutorialName}
        topic={topic}
        pipelineLoading={pipelineLoading}
        projectId={projectId}
        onRunStep={(stepId) => runPipelineStep(stepId, {
          persistTree: stepId === 'concept_tree_v3' && Boolean(projectId),
        })}
        onRunFromStep={runPipelineFrom}
      />
    </div>
  );
}

export default TutorialFlow3Page;
